import React from 'react';
import {
  Grid,
  Theme,
  Typography,
  createStyles,
  makeStyles,
  useMediaQuery,
  useTheme,
} from '@material-ui/core';
import { DefaultButton, Modal, colors } from 'design-system';
import { ArrowRight } from 'react-feather';
import LargeCircleCheckIcon from 'src/components/Icons/LargeCircleCheckIcon';
import CircleDeleteIcon from 'src/components/Icons/CircleDeleteIcon';
import { REQUEST_UPSELLING } from 'src/store/emissionPolicy/requestIdentifiers';
import { useNetworkObserver } from 'data-layer-cr/dist/network';
import NetworkIndicator from 'src/components/general/NetworkIndicator';
import useBaseWrapperStyles from '../../useBaseWrapperStyles';

const nullBorder = 'none 0px transparent';
const paddingZero = '0px !important';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    formStyle: {
      paddingRight: 12,
      paddingLeft: 16,
    },
    blackTitle: {
      fontSize: 19,
      color: '#555659',
      textAlign: 'right',
    },
    greenTitle: {
      fontSize: 19,
      color: '#27AE60',
      textAlign: 'left',
    },
    gridLine: {
      marginBottom: 15,
    },
    titleItem: {
      paddingRight: paddingZero,
      paddingLeft: paddingZero,
    },
    divider: {
      width: '100%',
      borderTop: 'dotted 2px #A3A2A2',
      borderBottom: nullBorder,
      borderLeft: nullBorder,
      borderRight: nullBorder,
      marginBottom: 15,
      marginTop: 15,
    },
    normalText: {
      fontSize: 15,
      fontWeight: 400,
      color: '#4A4B4E',
    },
    greenText: {
      fontSize: 17,
      color: '#27AE60',
    },
    gridRow: {
      padding: '0px !important',
      display: 'flex',
    },
    grayHeader: {
      backgroundColor: '#F2F2F2',
      fontSize: 17,
      color: '#555659',
      textAlign: 'center',
      fontWeight: 700,
      height: 42,
      margin: 'auto',
      paddingTop: 7,
    },
    greenHeader: {
      backgroundColor: '#27AE60',
      fontSize: 17,
      color: '#FFFFFF',
      textAlign: 'center',
      fontWeight: 700,
      height: 42,
      paddingTop: 7,
    },
    checkContent: {
      backgroundColor: '#27AE600D',
      textAlign: 'center',
      [theme.breakpoints.up('lg')]: {
        height: 85,
      },
      [theme.breakpoints.up('xl')]: {
        height: 85,
      },
      [theme.breakpoints.up('sm')]: {
        height: 60,
      },
      paddingTop: 16,
    },
    noCoverageContent: {
      backgroundColor: '#F7F7F7',
      textAlign: 'center',
      [theme.breakpoints.up('lg')]: {
        height: 85,
      },
      [theme.breakpoints.up('xl')]: {
        height: 85,
      },
      [theme.breakpoints.up('sm')]: {
        height: 60,
      },
      paddingTop: 16,
    },
    descriptionContent: {
      textAlign: 'left',
      [theme.breakpoints.up('lg')]: {
        height: 85,
      },
      [theme.breakpoints.up('xl')]: {
        height: 85,
      },
      [theme.breakpoints.up('sm')]: {
        height: 60,
      },
      paddingTop: 14,
    },
  }),
);

type Props = {
  onClose: () => void;
  open: boolean;
  moneyType: string | undefined;
  onUpselling: () => void;
};

const UpsellingModal = ({
  open,
  onClose,
  moneyType,
  onUpselling,
}: Props): JSX.Element => {
  const classes = useStyles();
  const wrapperClasses = useBaseWrapperStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const price = moneyType === '$' ? '2.24' : '1,118.7';
  const upsellingStatus = useNetworkObserver(REQUEST_UPSELLING);
  return (
    <Modal
      data-testid="swi-upselling-modal"
      title="¡Adquiere más beneficios mejorando tu endoso!"
      open={open}
      maxWidth="lg"
      onCloseRequested={onClose}
    >
      <form data-testid="form" className={classes.formStyle}>
        <Grid container spacing={3}>
          <Grid item xs={5} className={classes.titleItem}>
            <Grid container alignItems="flex-end" justifyContent="flex-end">
              <Typography
                data-testid="dorado-title-testid"
                className={classes.blackTitle}
              >
                Endoso dorado
              </Typography>
            </Grid>
          </Grid>
          <Grid item xs={2} className={classes.titleItem}>
            <Grid container alignItems="center" justifyContent="center">
              <Typography variant="caption">
                <ArrowRight color="#27AE60" />
              </Typography>
            </Grid>
          </Grid>
          <Grid item xs={5} className={classes.titleItem}>
            <Grid container alignItems="flex-start" justifyContent="flex-start">
              <Typography
                className={classes.greenTitle}
                data-testid="platino-title"
              >
                Endoso platino
              </Typography>
            </Grid>
          </Grid>
        </Grid>
        <hr className={classes.divider} />
        <Grid container spacing={1} className={classes.gridLine}>
          <Typography
            className={classes.normalText}
            data-testid="price-upselling-data"
          >
            Por{' '}
            <b className={classes.greenText}>
              {moneyType}
              {price}
            </b>{' '}
            adicionales al mes obtén los siguientes beneficios:
          </Typography>
        </Grid>
        <Grid container spacing={1} style={{ maxWidth: 600 }}>
          <Grid item xs={12} className={classes.gridRow}>
            <Grid item xs={6}>
              &nbsp;
            </Grid>
            <Grid item xs={3} className={classes.grayHeader}>
              Dorado
            </Grid>
            <Grid item xs={3} className={classes.greenHeader}>
              Platino
            </Grid>
          </Grid>
          <Grid item xs={12} className={classes.gridRow}>
            <Grid item xs={6} className={classes.descriptionContent}>
              <Typography className={classes.normalText}>
                {' '}
                <b>Alquiler de auto</b> por comprensivo{' '}
              </Typography>
            </Grid>
            <Grid item xs={3} className={classes.noCoverageContent}>
              <CircleDeleteIcon color="#555659" />
            </Grid>
            <Grid item xs={3} className={classes.checkContent}>
              <LargeCircleCheckIcon color="#27AE60" />
            </Grid>
          </Grid>
          <Grid item xs={12} className={classes.gridRow}>
            <Grid item xs={6} className={classes.descriptionContent}>
              <Typography className={classes.normalText}>
                Un <b>400%</b> más de monto de cobertura en{' '}
                <b>robo de artículos personales</b>
              </Typography>
            </Grid>
            <Grid item xs={3} className={classes.noCoverageContent}>
              <CircleDeleteIcon color="#555659" />
            </Grid>
            <Grid item xs={3} className={classes.checkContent}>
              <LargeCircleCheckIcon color="#27AE60" />
            </Grid>
          </Grid>
          <Grid item xs={12} className={classes.gridRow}>
            <Grid item xs={6} className={classes.descriptionContent}>
              <Typography className={classes.normalText}>
                Un <b>+50%</b> de monto de cobertura en la{' '}
                <b>póliza de vida accidental</b>
              </Typography>
            </Grid>
            <Grid item xs={3} className={classes.noCoverageContent}>
              <CircleDeleteIcon color="#555659" />
            </Grid>
            <Grid item xs={3} className={classes.checkContent}>
              <LargeCircleCheckIcon color="#27AE60" />
            </Grid>
          </Grid>
        </Grid>
        <hr className={classes.divider} />
        <Grid item container xs={12}>
          <Grid
            item
            xs={12}
            lg={6}
            container
            justifyContent="center"
            style={{ textAlign: 'center' }}
          >
            <div className={wrapperClasses.buttonContainer}>
              <DefaultButton
                id="dorado-button"
                data-testid="dorado-button"
                fullWidth={isMobile}
                forcedWidth={!isMobile ? 250 : undefined}
                onClick={(): void => {
                  onClose();
                }}
                variant="outlined"
                customColor={{
                  background: colors.white,
                  default: colors.bright_turquoise,
                  hover: colors.onahau,
                  active: colors.assa_blue,
                }}
                disabled={upsellingStatus === 'requesting'}
              >
                CONSERVAR DORADO
              </DefaultButton>
            </div>
          </Grid>

          <Grid
            item
            xs={12}
            lg={6}
            container
            justifyContent="center"
            style={{ textAlign: 'center' }}
          >
            <div className={wrapperClasses.buttonContainer}>
              <DefaultButton
                id="platino-button"
                data-testid="platino-button"
                fullWidth={isMobile}
                forcedWidth={!isMobile ? 250 : undefined}
                onClick={(): void => {
                  onUpselling();
                }}
                variant="outlined"
                customColor={{
                  background: colors.bright_turquoise,
                  default: colors.white,
                  hover: colors.assa_blue,
                  active: colors.assa_blue,
                }}
                disabled={upsellingStatus === 'requesting'}
              >
                CAMBIAR A PLATINO
              </DefaultButton>
            </div>
          </Grid>
        </Grid>
        <Grid item container xs={12}>
          <Grid style={{ margin: 'auto' }}>
            <NetworkIndicator
              data-testid="NetworkIndicatorComponent"
              message="Actualizando endoso... modificando tarifa..."
              requesting={upsellingStatus === 'requesting'}
              variant="horizontal"
              textColor="#4A4B4E"
              spinnerColor={colors.bright_turquoise}
            />
          </Grid>
        </Grid>
      </form>
    </Modal>
  );
};

export default UpsellingModal;
